import { z } from 'zod';

import type { FilterValue, NegatedFilterValue } from '../createColumnBuilder';

export function isNegatedFilterValue(value: unknown): value is NegatedFilterValue {
  return typeof value === 'object' && value !== null && 'not' in value && Array.isArray(value.not);
}

/** A multi-select filter's selected values, and whether a `filterNegatable` column excludes them. */
export function parseMultiSelectFilterValue(value: unknown): {
  values: string[];
  negated: boolean;
} {
  if (isNegatedFilterValue(value)) return { values: value.not, negated: true };
  return { values: Array.isArray(value) ? value : [], negated: false };
}

export function multiSelectFilterValue(
  values: string[],
  negated: boolean,
): string[] | NegatedFilterValue {
  return negated ? { not: values } : values;
}

export const numberRangeFilterSchema = z.tuple([z.number(), z.number()]);

const dateRangeFilterSchema = z
  .tuple([z.string().nullish(), z.string().nullish()])
  .transform(([start, end]) => (start || end ? { start, end } : undefined));

export function parseDateRangeFilter(value: unknown) {
  const result = dateRangeFilterSchema.safeParse(value);
  return result.success ? result.data : undefined;
}

export function isFilterApplied(value: FilterValue): boolean {
  if (typeof value === 'boolean') {
    return true;
  }
  if (isNegatedFilterValue(value)) {
    return value.not.length > 0;
  }
  if (Array.isArray(value)) {
    if (value[0] === null && value[1] === null) {
      return false;
    }
    return value?.length > 0;
  }
  if (typeof value === 'number') {
    return true;
  }
  if (value === null) {
    return false;
  }
  return value?.length > 0;
}
